// Cognito sign-in via SRP, with no Hosted UI.
//
// The Hosted UI would need a Cognito domain and an OAuth redirect flow for what
// is a private two-person tool. SRP against the user pool directly is a few
// dozen lines and keeps the whole app a static bundle with no callback routes.
//
// The pool is AllowAdminCreateUserOnly, so the very first sign-in always lands
// on the NEW_PASSWORD_REQUIRED challenge - that path is not an edge case here,
// it is the normal first run.

import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

const pool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_USER_POOL_ID,
  ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
});

export type SignInResult =
  | { kind: 'signedIn' }
  | { kind: 'newPasswordRequired'; submit: (password: string) => Promise<void> };

export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool });
    const details = new AuthenticationDetails({ Username: email, Password: password });

    user.authenticateUser(details, {
      onSuccess: () => resolve({ kind: 'signedIn' }),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        resolve({
          kind: 'newPasswordRequired',
          submit: (next: string) =>
            new Promise<void>((res, rej) => {
              // Cognito rejects the challenge if immutable attributes are
              // echoed back, so pass an empty attribute set.
              user.completeNewPasswordChallenge(next, {}, {
                onSuccess: () => res(),
                onFailure: (e) => rej(e),
              });
            }),
        });
      },
    });
  });
}

// Resolves the current session, refreshing it if the access token has expired.
export function getSession(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      resolve(err || !session?.isValid() ? null : session);
    });
  });
}

export async function getIdToken(): Promise<string | null> {
  const session = await getSession();
  return session ? session.getIdToken().getJwtToken() : null;
}

export function signOut() {
  pool.getCurrentUser()?.signOut();
}

export async function currentEmail(): Promise<string | null> {
  const session = await getSession();
  return session ? (session.getIdToken().payload.email as string) ?? null : null;
}
